import { Assignment, Business, Folder, TrendingUp } from "@mui/icons-material";
import { Box, CircularProgress, Divider, Grid2, Paper, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { getAdminCounters, getRecentlyBlockedUsers, getValidSessionUsers } from "../../api/app";
import { useTranslationHook } from "../../hooks";
import { AdminCounters } from "../../types/model/stats";
import { User } from "../../types/model/user";
import { StoreInterfaces } from "../../types/store/StoreInterfaces";
import UsersTable from "../Users/UsersTable";
import StatCard from "./StatCard";

const AdminDashboard = () => {
  const { t } = useTranslationHook();

  const [counters, setCounters] = useState<AdminCounters>();
  const [countersLoading, setCountersLoading] = useState(false);

  const [blockedUsers, setBlockedUsers] = useState<User[]>([]);
  const [blockedUsersLoading, setBlockedUsersLoading] = useState(false);

  const [validSessionUsers, setValidSessionUsers] = useState<User[]>([]);
  const [validSessionUsersLoading, setValidSessionUsersLoading] =
    useState(false);

  const navigate = useNavigate();
  const session = useSelector((state: StoreInterfaces) => state.session);

  const fetchCounters = async () => {
    setCountersLoading(true);
    try {
      const data = await getAdminCounters();
      setCounters(data);
    } catch (error) {
      console.error(error);
    } finally {
      setCountersLoading(false);
    }
  };

  const fetchRecentlyBlockedUsers = async () => {
    setBlockedUsersLoading(true);
    try {
      const data = await getRecentlyBlockedUsers();
      setBlockedUsers(data);
    } catch (error) {
      console.error(error);
    } finally {
      setBlockedUsersLoading(false);
    }
  };

  const fetchValidSessionUsers = async () => {
    setValidSessionUsersLoading(true);
    try {
      const data = await getValidSessionUsers();
      setValidSessionUsers(data);
    } catch (error) {
      console.error(error);
    } finally {
      setValidSessionUsersLoading(false);
    }
  };

  useEffect(() => {
    fetchCounters();
    fetchRecentlyBlockedUsers();
    fetchValidSessionUsers();
  }, []);

  const handleRequestRefreshBlockedUsers = () => {
    fetchRecentlyBlockedUsers();
  };

  const handleRequestRefreshValidSessionUsers = () => {
    fetchValidSessionUsers();
  };

  const handleUserRowClick = (user: User) => {
    if (session.isAdministrator) {
      navigate(`/admin/accounts/${user.accountId}/users/${user.id}`);
    }
  };

  let countersContent;
  if (countersLoading) {
    countersContent = (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  } else if (counters) {
    countersContent = (
      <Grid2
        container
        spacing={2}
        sx={{ mb: (theme) => theme.spacing(4) }}
      >
        <Grid2 container spacing={2} justifyContent="center" size={12}>
          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
            <StatCard
              icon={<Business />}
              title={t("generic.stats.accounts")}
              total={counters.accounts.total}
              active={counters.accounts.active}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
            <StatCard
              icon={<Business />}
              title={t("generic.stats.users")}
              total={counters.users.total}
              active={counters.users.active}
            />
          </Grid2>
        </Grid2>

        <Grid2 container spacing={2} size={12}>
          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
            <StatCard
              icon={<Folder />}
              title={t("generic.stats.projects")}
              total={counters.projects.total}
              active={counters.projects.active}
              closed={counters.projects.closed}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
            <StatCard
              icon={<TrendingUp />}
              title={t("generic.stats.activities")}
              total={counters.activities.total}
              active={counters.activities.active}
              closed={counters.activities.closed}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
            <StatCard
              icon={<Assignment />}
              title={t("generic.stats.procedures")}
              total={counters.procedures.total}
              active={counters.procedures.active}
              closed={counters.procedures.closed}
            />
          </Grid2>
        </Grid2>
      </Grid2>
    );
  } else {
    countersContent = (
      <Typography align="center" sx={{ mt: 2 }}>
        {t("generic.stats.no-data")}
      </Typography>
    );
  }

  let blockedUsersContent;
  if (blockedUsersLoading) {
    blockedUsersContent = (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  } else if (blockedUsers.length > 0) {
    blockedUsersContent = (
      <>
        <Typography variant="h4" sx={{ mb: 3, color: "text.secondary" }}>
          {t("generic.stats.blocked-users")}
        </Typography>
        <UsersTable
          tableData={blockedUsers}
          onRequestRefresh={handleRequestRefreshBlockedUsers}
          onRowClick={handleUserRowClick}
        />
      </>
    );
  }

  let validSessionUsersContent;
  if (validSessionUsersLoading) {
    validSessionUsersContent = (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  } else if (validSessionUsers.length > 0) {
    validSessionUsersContent = (
      <>
        <Typography variant="h4" sx={{ mb: 3, color: "text.secondary" }}>
          {t("generic.stats.valid-session-users")}
        </Typography>
        <UsersTable
          tableData={validSessionUsers}
          onRequestRefresh={handleRequestRefreshValidSessionUsers}
          onRowClick={handleUserRowClick}
        />
      </>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {/* Counters */}
      <Paper
        sx={{
          p: 4,
          borderRadius: 3,
          boxShadow: 2,
        }}
      >
        <Typography variant="h4" sx={{ mb: 3 }}>
          {t("generic.stats.overview")}
        </Typography>

        {countersContent}

        <Divider sx={{ my: 1 }} />

        {/* Recently Blocked Users Table */}
        {blockedUsersContent}

        {/* Valid Session Users Table */}
        {validSessionUsersContent}
      </Paper>
    </Box>
  );
};

export default AdminDashboard;
